import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Search, FileText, Clock, CheckCircle2, XCircle, Layers, Eye, Wheat, KeyRound } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "", label: "सर्व" },
  { key: "SUBMITTED", label: "सादर केलेले" },
  { key: "APPROVED", label: "मंजूर" },
  { key: "REJECTED", label: "नामंजूर" },
  { key: "DRAFT", label: "प्रारूप" },
];

const STATUS_BADGE = {
  SUBMITTED: "bg-blue-50 text-blue-700 border-blue-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED: "bg-red-50 text-red-700 border-red-200",
  DRAFT: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_LABEL = { SUBMITTED: "सादर केला", APPROVED: "मंजूर", REJECTED: "नामंजूर", DRAFT: "प्रारूप" };

const ACTION_LABEL = { document_view: "कागदपत्र पाहिले", data_export: "यादी डाउनलोड/export" };

function SettingsView() {
  const [startDate, setStartDate] = useState("");
  const [lastDate, setLastDate] = useState("");
  const [extendedDate, setExtendedDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);
  useEffect(() => {
    api.getDeadline().then((d) => {
      setState(d);
      setStartDate((d?.startDate || "").slice(0, 16));
      setLastDate((d?.lastDate || "").slice(0, 16));
      setExtendedDate((d?.extendedDate || "").slice(0, 16));
    }).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const d = await api.setDeadline({ startDate: startDate || null, lastDate: lastDate || null, extendedDate: extendedDate || null });
      setState(d);
      toast.success("सेटिंग्ज जतन झाली.");
    } catch (e) { toast.error("त्रुटी: " + (e.message || "")); }
    setSaving(false);
  };
  return (
    <div className="max-w-xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-settings">
      <h3 className="mb-1 text-[16px] font-bold text-[#1b5e3f]">अर्ज मुदत सेटिंग्ज</h3>
      <p className="mb-5 text-[12.5px] text-slate-500">वेळ भारतीय प्रमाण वेळेनुसार (IST). मुदतवाढ भरल्यास ती मूळ अंतिम तारखेला ओव्हरराइड करते.</p>
      <label className="mb-1.5 block text-[13.5px] font-semibold text-slate-700">अर्ज सुरू करण्याची वेळ (तारीख व वेळ)</label>
      <input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="settings-start-date"
        className="mb-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f]" />
      <p className="mb-4 text-[12px] text-slate-500">या वेळेपूर्वी "नवीन अर्ज सुरू करा" बटण निष्क्रिय राहील. रिकामे ठेवल्यास अर्ज लगेच सुरू राहतील.</p>
      <label className="mb-1.5 block text-[13.5px] font-semibold text-slate-700">अर्ज करण्याची अंतिम तारीख व वेळ</label>
      <input type="datetime-local" value={lastDate} onChange={(e) => setLastDate(e.target.value)} data-testid="settings-last-date"
        className="mb-4 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f]" />
      <label className="mb-1.5 block text-[13.5px] font-semibold text-slate-700">मुदतवाढ तारीख व वेळ (ऐच्छिक)</label>
      <input type="datetime-local" value={extendedDate} onChange={(e) => setExtendedDate(e.target.value)} data-testid="settings-ext-date"
        className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f]" />
      {extendedDate && <p className="mb-4 text-[12px] font-semibold text-[#c2570a]">मुदतवाढ लागू — बॅनर व सर्व मुदती नवीन तारखेनुसार चालतील.</p>}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={saving || (!lastDate && !startDate)} data-testid="settings-save"
          className="rounded-full bg-[#1b5e3f] px-6 py-2.5 text-[14px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-50">जतन करा</button>
        {state?.notStarted && (
          <span className="text-[12.5px] font-semibold text-[#c2570a]" data-testid="settings-not-started">अर्ज अद्याप सुरू झालेले नाहीत</span>
        )}
        {state?.configured && !state?.notStarted && (
          <span className={`text-[12.5px] font-semibold ${state.passed ? "text-red-600" : "text-emerald-700"}`}>
            {state.passed ? "मुदत संपली आहे" : "मुदत सुरू आहे"}
          </span>
        )}
      </div>
    </div>
  );
}

function AuditView() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    setLoading(true);
    api.adminAuditLogs(filter).then((d) => setLogs(d || [])).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        {[["", "सर्व"], ["document_view", "कागदपत्र पाहणे"], ["data_export", "यादी export"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`audit-filter-${k || "all"}`}
            className={`rounded-full border px-4 py-1.5 text-[13px] font-medium ${filter === k ? "border-[#1b5e3f] bg-[#1b5e3f] text-white" : "border-slate-300 bg-white text-slate-600 hover:border-[#1b5e3f]"}`}>{l}</button>
        ))}
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="admin-audit-table">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-slate-50 text-left text-slate-500">
                <th className="px-4 py-3 font-semibold">दिनांक व वेळ</th>
                <th className="px-4 py-3 font-semibold">अधिकारी</th>
                <th className="px-4 py-3 font-semibold">भूमिका</th>
                <th className="px-4 py-3 font-semibold">कृती</th>
                <th className="px-4 py-3 font-semibold">तपशील</th>
                <th className="px-4 py-3 font-semibold">IP पत्ता</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">लोड होत आहे...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">कोणतीही नोंद नाही.</td></tr>
              ) : (
                logs.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-latin text-[12.5px] text-slate-600">{l.at ? new Date(l.at).toLocaleString("en-GB") : "—"}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{l.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{l.role || "—"}</td>
                    <td className="px-4 py-3"><span className="rounded-full bg-[#eef6f0] px-2.5 py-0.5 text-[12px] font-semibold text-[#1b5e3f]">{ACTION_LABEL[l.action] || l.action}</span></td>
                    <td className="px-4 py-3 text-[12px] text-slate-500">{l.meta?.scope || l.meta?.applicationId || l.meta?.fileId || (l.meta?.kind ? `${l.meta.kind} (${l.meta.count || 0})` : "—")}</td>
                    <td className="px-4 py-3 font-latin text-[12.5px] text-slate-500">{l.ip || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StorageView() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);
  const [clearing, setClearing] = useState(false);
  const load = () => {
    setLoading(true);
    api.adminStorageStats().then((d) => setStats(d)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);
  const doBackup = async () => {
    setBackingUp(true);
    try {
      const r = await api.adminBackup();
      toast.success(`बॅकअप डाउनलोड झाला (${(r.size / 1024).toFixed(0)} KB)`);
    } catch (e) { toast.error("त्रुटी: " + (e.message || "")); }
    setBackingUp(false);
  };
  const doClear = async () => {
    const typed = window.prompt('⚠️ ही कृती सर्व अर्ज, फाईल-नोंदी, ऑडिट लॉग व अर्जदार खाती कायमची हटवेल (अधिकारी खाती व सेटिंग्ज राहतील). पुष्टीसाठी खालील मजकूर जसाच्या तसा टाइप करा:\n\nCLEAR-ALL-DEMO-DATA');
    if (typed === null) return;
    if (typed.trim() !== "CLEAR-ALL-DEMO-DATA") { toast.error("पुष्टीकरण मजकूर जुळला नाही. कृती रद्द."); return; }
    setClearing(true);
    try {
      const r = await api.adminClearApplicationData("CLEAR-ALL-DEMO-DATA");
      const d = r.deleted || {};
      toast.success(`डेटा साफ झाला — अर्ज: ${d.applications}, फाईल्स: ${d.files}, अर्जदार: ${d.applicantUsers}`);
      load();
    } catch (e) { toast.error("त्रुटी: " + (e.message || "")); }
    setClearing(false);
  };
  const cards = stats ? [
    { label: "एकूण फाईल्स", value: stats.fileCount, sub: "कागदपत्रे व फोटो" },
    { label: "वापरलेली जागा", value: stats.totalGB >= 1 ? `${stats.totalGB} GB` : `${stats.totalMB} MB`, sub: `${stats.totalBytes.toLocaleString()} bytes` },
    { label: "ऑब्जेक्ट स्टोरेजमध्ये", value: stats.inObjectStore, sub: stats.legacyInDb ? `${stats.legacyInDb} अजून DB मध्ये` : "सर्व स्थलांतरित" },
    { label: "एकूण अर्ज", value: stats.applications, sub: "डेटाबेस नोंदी" },
  ] : [];
  return (
    <div data-testid="admin-storage">
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {loading ? (
          <div className="col-span-full py-10 text-center text-slate-400">लोड होत आहे...</div>
        ) : cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`storage-card-${c.label}`}>
            <p className="text-[24px] font-bold text-[#1b5e3f] font-latin">{c.value}</p>
            <p className="text-[13px] font-semibold text-slate-700">{c.label}</p>
            <p className="text-[11.5px] text-slate-400">{c.sub}</p>
          </div>
        ))}
      </div>
      <div className="max-w-xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="mb-1 text-[16px] font-bold text-[#1b5e3f]">डेटाबेस बॅकअप</h3>
        <p className="mb-4 text-[12.5px] text-slate-500">सर्व अर्ज, फाईल-नोंदी, सेटिंग्ज व ऑडिट लॉग एका JSON फाईलमध्ये डाउनलोड करा. (फाईल्सचे प्रत्यक्ष कागदपत्र ऑब्जेक्ट स्टोरेजमध्ये सुरक्षित आहेत; पासवर्ड बॅकअपमध्ये समाविष्ट नाहीत.)</p>
        <div className="flex gap-3">
          <button onClick={doBackup} disabled={backingUp} data-testid="storage-backup-btn"
            className="rounded-full bg-[#1b5e3f] px-6 py-2.5 text-[14px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-50">
            {backingUp ? "तयार होत आहे..." : "बॅकअप डाउनलोड करा"}
          </button>
          <button onClick={load} className="rounded-full border border-slate-300 px-5 py-2.5 text-[14px] font-semibold text-slate-600 hover:bg-slate-50">रिफ्रेश</button>
        </div>
      </div>
      <div className="mt-5 max-w-xl rounded-xl border-2 border-red-200 bg-red-50/60 p-6 shadow-sm" data-testid="admin-danger-zone">
        <h3 className="mb-1 text-[16px] font-bold text-red-700">डेमो डेटा साफ करा (धोका क्षेत्र)</h3>
        <p className="mb-4 text-[12.5px] text-red-600/90">सर्व अर्ज, फाईल-नोंदी, ऑडिट लॉग व अर्जदार खाती कायमची हटवली जातील आणि अर्ज क्रमांक पुन्हा 133001 पासून सुरू होईल. अधिकारी/प्रशासक खाती व मुदत सेटिंग्ज राहतील. ही कृती परत करता येणार नाही.</p>
        <button onClick={doClear} disabled data-testid="storage-clear-btn" title="ही सुविधा सध्या बंद केली आहे"
          className="rounded-full bg-red-600 px-6 py-2.5 text-[14px] font-semibold text-white opacity-50 cursor-not-allowed">
          {clearing ? "साफ होत आहे..." : "सर्व डेमो डेटा साफ करा"}
        </button>
        <p className="mt-2 text-[12px] font-semibold text-slate-500">ही सुविधा सध्या बंद केली आहे.</p>
      </div>
    </div>
  );
}

function CircleOfficersView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const load = () => {
    setLoading(true);
    api.adminCircleOfficers().then(setData).catch((e) => toast.error("त्रुटी: " + (e.message || ""))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const term = q.trim().toLowerCase();
  const offices = (data?.offices || [])
    .map((o) => ({
      ...o,
      officers: o.officers.filter((x) =>
        !term ||
        (x.username || "").toLowerCase().includes(term) ||
        (x.circle || "").toLowerCase().includes(term) ||
        (x.office || "").toLowerCase().includes(term) ||
        (x.tehsil || "").toLowerCase().includes(term)),
    }))
    .filter((o) => o.officers.length > 0);

  const downloadCsv = () => {
    const rows = [["वापरकर्तानाव", "तहसील कार्यालय", "मंडळ (Circle)", "तालुका", "अधिकार क्षेत्र", "स्थिती"]];
    (data?.offices || []).forEach((o) => o.officers.forEach((x) => rows.push([
      x.username, o.office, x.circle || "", x.tehsil || "", x.jurisdiction || "",
      x.active === false ? "निष्क्रिय" : "सक्रिय",
    ])));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "circle-officers.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("यादी डाउनलोड झाली");
  };

  return (
    <div data-testid="admin-circle-officers">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-[17px] font-bold text-[#1b5e3f]">मंडळ अधिकारी लॉगिन यादी</h3>
          <p className="text-[12.5px] text-slate-500">
            एकूण {data?.totalOfficers ?? "—"} मंडळ अधिकारी · {data?.totalOffices ?? "—"} तहसील कार्यालये.
            सर्व लॉगिन <span className="font-latin font-semibold">co_</span> ने सुरू होतात. पहिल्या लॉगिनला पासवर्ड बदलणे आवश्यक.
          </p>
        </div>
        <button onClick={downloadCsv} data-testid="circle-officers-csv-btn"
          className="rounded-full bg-[#1b5e3f] px-5 py-2.5 text-[13.5px] font-semibold text-white hover:bg-[#144d33]">
          CSV डाउनलोड करा
        </button>
      </div>
      <div className="relative mb-5 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="circle-officers-search"
          placeholder="मंडळ / वापरकर्तानाव / तहसील शोधा..."
          className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-[14px] outline-none focus:border-[#1b5e3f]" />
      </div>
      {loading ? (
        <div className="py-16 text-center text-slate-400">लोड होत आहे...</div>
      ) : offices.length === 0 ? (
        <div className="py-16 text-center text-slate-400">कोणतेही मंडळ अधिकारी सापडले नाहीत.</div>
      ) : (
        <div className="space-y-6">
          {offices.map((o) => (
            <div key={o.office} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`office-group-${o.office}`}>
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
                <h4 className="text-[14.5px] font-bold text-[#1b5e3f]">{o.office}</h4>
                <span className="rounded-full bg-[#1b5e3f]/10 px-3 py-0.5 text-[12px] font-semibold text-[#1b5e3f]">{o.officers.length} मंडळे</span>
              </div>
              <table className="w-full text-left text-[13.5px]">
                <thead>
                  <tr className="border-b border-slate-100 text-[11.5px] uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-2 font-semibold">वापरकर्तानाव</th>
                    <th className="px-4 py-2 font-semibold">मंडळ (Circle)</th>
                    <th className="px-4 py-2 font-semibold">तालुका</th>
                    <th className="px-4 py-2 font-semibold">स्थिती</th>
                  </tr>
                </thead>
                <tbody>
                  {o.officers.map((x) => (
                    <tr key={x.username} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60" data-testid={`co-row-${x.username}`}>
                      <td className="px-4 py-2 font-latin font-semibold text-slate-800">{x.username}</td>
                      <td className="px-4 py-2 text-slate-700">{x.circle || "—"}</td>
                      <td className="px-4 py-2 text-slate-500">{x.tehsil || "—"}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded-full border px-2 py-0.5 text-[11.5px] font-semibold ${x.active === false ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                          {x.active === false ? "निष्क्रिय" : "सक्रिय"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast.error("नवीन पासवर्ड किमान 8 अक्षरांचा असावा."); return; }
    if (nw !== conf) { toast.error("नवीन पासवर्ड व पुष्टीकरण जुळत नाही."); return; }
    setBusy(true);
    try {
      await api.adminChangePassword(cur, nw);
      toast.success("पासवर्ड यशस्वीरित्या बदलला.");
      onClose();
    } catch (err) { toast.error("त्रुटी: " + (err.message || "")); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="admin-pw-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="mb-1 text-[17px] font-bold text-[#1b5e3f]">पासवर्ड बदला</h3>
        <p className="mb-4 text-[12.5px] text-slate-500">आपल्या प्रशासक खात्याचा पासवर्ड बदला.</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="mb-1 block text-[13px] font-semibold text-slate-700">सध्याचा पासवर्ड</label>
            <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="pw-current"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f]" />
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-semibold text-slate-700">नवीन पासवर्ड (किमान 8 अक्षरे)</label>
            <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} required data-testid="pw-new"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f]" />
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-semibold text-slate-700">नवीन पासवर्ड पुन्हा टाइप करा</label>
            <input type="password" value={conf} onChange={(e) => setConf(e.target.value)} required data-testid="pw-confirm"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f]" />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={onClose} data-testid="pw-cancel"
              className="rounded-full border border-slate-300 px-5 py-2.5 text-[14px] font-semibold text-slate-600 hover:bg-slate-50">रद्द करा</button>
            <button type="submit" disabled={busy} data-testid="pw-submit"
              className="rounded-full bg-[#1b5e3f] px-6 py-2.5 text-[14px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-50">
              {busy ? "बदलत आहे..." : "पासवर्ड बदला"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("apps");
  const [showPwModal, setShowPwModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([api.adminStats(), api.adminList(search, status)]);
      setStats(s);
      setRows(list);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const doLogout = () => { logout(); navigate("/login"); };

  const statCards = stats
    ? [
        { label: "एकूण अर्ज", value: stats.total, Icon: Layers, cls: "text-[#1b5e3f] bg-blue-50" },
        { label: "सादर केलेले", value: stats.submitted, Icon: Clock, cls: "text-blue-600 bg-blue-50" },
        { label: "मंजूर", value: stats.approved, Icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
        { label: "नामंजूर", value: stats.rejected, Icon: XCircle, cls: "text-red-600 bg-red-50" },
        { label: "प्रारूप", value: stats.draft, Icon: FileText, cls: "text-slate-600 bg-slate-100" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#f6f2e6]">
      {/* Admin header */}
      <header className="sticky top-0 z-40 text-white shadow-md" style={{ background: "linear-gradient(120deg,#1b5e3f 0%,#2d7a52 100%)" }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#E0A106] bg-white">
              <Wheat className="h-5 w-5 text-[#1b5e3f]" strokeWidth={2} />
            </div>
            <div>
              <p className="text-[16px] font-bold leading-tight">ASSK Amravati — कार्यालय प्रशासन</p>
              <p className="text-[12px] opacity-80">जिल्हा सेतू समिती, अमरावती</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-[13px] opacity-90 sm:inline">{user?.email}</span>
            <button onClick={() => setShowPwModal(true)} data-testid="admin-change-pw-btn"
              className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-[13px] font-medium hover:bg-white/20">
              <KeyRound className="h-4 w-4" /> पासवर्ड बदला
            </button>
            <button onClick={doLogout} className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-[13px] font-medium hover:bg-white/20">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>
      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}

      <div className="mx-auto max-w-6xl px-5 py-6">
        <div className="mb-5 flex flex-wrap gap-2">
          <button onClick={() => setView("apps")} data-testid="admin-tab-apps"
            className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${view === "apps" ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>अर्ज</button>
          <button onClick={() => setView("circle")} data-testid="admin-tab-circle"
            className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${view === "circle" ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>मंडळ अधिकारी</button>
          <button onClick={() => setView("audit")} data-testid="admin-tab-audit"
            className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${view === "audit" ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>ऑडिट लॉग</button>
          <button onClick={() => setView("settings")} data-testid="admin-tab-settings"
            className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${view === "settings" ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>सेटिंग्ज</button>
          <button onClick={() => setView("storage")} data-testid="admin-tab-storage"
            className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${view === "storage" ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>स्टोरेज व बॅकअप</button>
        </div>
        {view === "settings" ? <SettingsView /> : view === "audit" ? <AuditView /> : view === "storage" ? <StorageView /> : view === "circle" ? <CircleOfficersView /> : (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {statCards.map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-lg ${c.cls}`}>
                <c.Icon className="h-5 w-5" />
              </div>
              <p className="text-[24px] font-bold text-slate-800 font-latin">{c.value}</p>
              <p className="text-[13px] text-slate-500">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Search + filter */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="नाव / ई-मेल / अर्ज क्रमांक शोधा"
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-[14px] outline-none focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/15"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setStatus(t.key)}
                className={`rounded-full border px-4 py-1.5 text-[13px] font-medium transition-all ${
                  status === t.key ? "border-[#1b5e3f] bg-[#1b5e3f] text-white" : "border-slate-300 bg-white text-slate-600 hover:border-[#1b5e3f]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="bg-slate-50 text-left text-slate-500">
                  <th className="px-4 py-3 font-semibold">अर्ज क्रमांक</th>
                  <th className="px-4 py-3 font-semibold">अर्जदार</th>
                  <th className="px-4 py-3 font-semibold">संपर्क</th>
                  <th className="px-4 py-3 font-semibold">स्थिती</th>
                  <th className="px-4 py-3 font-semibold">अद्ययावत</th>
                  <th className="px-4 py-3 font-semibold text-right">कृती</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">लोड होत आहे...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">कोणतेही अर्ज सापडले नाहीत.</td></tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-bold text-[#1b5e3f] font-latin">{r.applicationNumber}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-800">{r.data?.nameMr || r.applicantName || "—"}</p>
                        <p className="text-[12px] text-slate-400">{r.applicantEmail}</p>
                      </td>
                      <td className="px-4 py-3 font-latin text-slate-600">{r.data?.mobile || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full border px-2.5 py-0.5 text-[12px] font-medium ${STATUS_BADGE[r.status]}`}>
                          {STATUS_LABEL[r.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-latin text-[12.5px] text-slate-500">
                        {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString("en-GB") : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate(`/admin/${r.id}`)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-[13px] font-medium text-[#1b5e3f] hover:bg-slate-50"
                        >
                          <Eye className="h-3.5 w-3.5" /> पहा
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
